import { useState } from 'react';
import { csvFilename, downloadCsv, resultsToCsv } from '../lib/resultsCsv.js';
import { download, resultsFilename, type ResultsSource } from '../lib/resultsFile.js';
import { resultsToXlsx } from '../lib/resultsXlsx.js';
import { track } from '../lib/track.js';

/**
 * Story #47, extended by #79 — takes the results already on screen away as a
 * file.
 *
 * One component for both views and both formats: the accumulated totals and a
 * single run differ only in whether there is a date to put in the first
 * column, and a reader opening any of these files should not have to learn a
 * second shape.
 *
 * No capability check. It exports what the page is already showing, so anyone
 * who got this far — Viewers included — may keep a copy of it.
 */
export function ExportResults({ source }: { source: ResultsSource }) {
  const empty = source.screens.every((screen) => screen.questions.length === 0);

  // The workbook writer is fetched on the first click, so the click has a
  // moment where nothing has happened yet. Without this the button looks
  // broken on a cold cache and invites a second click.
  const [building, setBuilding] = useState(false);

  async function exportXlsx() {
    setBuilding(true);
    try {
      download(resultsFilename(source, 'xlsx'), await resultsToXlsx(source));
      track('resultsExported');
    } finally {
      setBuilding(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="secondary"
        disabled={empty}
        onClick={() => {
          downloadCsv(csvFilename(source), resultsToCsv(source));
          // One key for both formats. Which file type an operator prefers is
          // not the question this dashboard exists to answer; whether anyone
          // takes their results away at all is.
          track('resultsExported');
        }}
      >
        Export CSV
      </button>
      <button
        type="button"
        className="secondary"
        disabled={empty || building}
        onClick={() => void exportXlsx()}
      >
        {building ? 'Preparing Excel…' : 'Export Excel'}
      </button>
    </>
  );
}
